import { useState } from "react";
import { useOrgSettings } from "@/lib/org-settings-context";
import { useQuery } from "@tanstack/react-query";
import { apiRequest } from "@/lib/queryClient";
import { Card, CardContent, CardDescription, CardHeader, CardTitle } from "@/components/ui/card";
import { Button } from "@/components/ui/button";
import { Skeleton } from "@/components/ui/skeleton";
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from "@/components/ui/select";
import {
  BarChart3,
  CreditCard,
  Users,
  DollarSign,
  Download,
  Calendar,
  FileSpreadsheet,
  Clock,
  Activity,
} from "lucide-react";
import { cn } from "@/lib/utils";
import { useToast } from "@/hooks/use-toast";

interface DashboardKPIs {
  totalCustomers: number;
  activeCards: number;
  totalTransactions: number;
  totalVolumeCents: number;
  pendingKyc: number;
  openTickets: number;
}

type DateRange = "today" | "7d" | "30d" | "90d" | "ytd" | "all";

const DATE_RANGE_OPTIONS: { value: DateRange; label: string }[] = [
  { value: "today", label: "Today" },
  { value: "7d", label: "Last 7 Days" },
  { value: "30d", label: "Last 30 Days" },
  { value: "90d", label: "Last 90 Days" },
  { value: "ytd", label: "Year to Date" },
  { value: "all", label: "All Time" },
];

function formatCompactNumber(num: number): string {
  if (num >= 1000000) {
    return (num / 1000000).toFixed(1) + "M";
  }
  if (num >= 1000) {
    return (num / 1000).toFixed(1) + "K";
  }
  return num.toString();
}

export default function ReportCenter() {
  const { toast } = useToast();
  const { formatCurrency } = useOrgSettings();
  const [dateRange, setDateRange] = useState<DateRange>("30d");
  const [isExporting, setIsExporting] = useState(false);

  const { data: kpis, isLoading } = useQuery<DashboardKPIs>({
    queryKey: ["reports-summary", dateRange],
    queryFn: async () => {
      const response = await apiRequest("GET", `/api/reports/summary?range=${dateRange}`);
      if (!response.ok) throw new Error("Failed to fetch report data");
      return response.json();
    },
  });

  const handleExportCSV = async (reportType: string) => {
    setIsExporting(true);
    try {
      // Generate CSV data based on report type
      let csvContent = "";
      let filename = "";

      switch (reportType) {
        case "summary":
          csvContent = generateSummaryCSV(kpis);
          filename = `report-summary-${dateRange}-${new Date().toISOString().split("T")[0]}.csv`;
          break;
        case "customers":
          csvContent = await generateCustomersCSV();
          filename = `customers-report-${new Date().toISOString().split("T")[0]}.csv`;
          break;
        case "cards":
          csvContent = await generateCardsCSV();
          filename = `cards-report-${new Date().toISOString().split("T")[0]}.csv`;
          break;
        case "transactions":
          csvContent = await generateTransactionsCSV();
          filename = `transactions-report-${new Date().toISOString().split("T")[0]}.csv`;
          break;
        default:
          throw new Error("Unknown report type");
      }

      // Download CSV
      const blob = new Blob([csvContent], { type: "text/csv;charset=utf-8;" });
      const link = document.createElement("a");
      link.href = URL.createObjectURL(blob);
      link.download = filename;
      link.click();
      URL.revokeObjectURL(link.href);

      toast({
        title: "Export Complete",
        description: `${filename} has been downloaded`,
      });
    } catch (error) {
      toast({
        title: "Export Failed",
        description: "Could not generate report. Please try again.",
        variant: "destructive",
      });
    } finally {
      setIsExporting(false);
    }
  };

  const generateSummaryCSV = (data: DashboardKPIs | undefined) => {
    if (!data) return "Metric,Value\n";
    return `Metric,Value
Total Customers,${data.totalCustomers}
Active Cards,${data.activeCards}
Total Transactions,${data.totalTransactions}
Total Volume (AUD),${(data.totalVolumeCents / 100).toFixed(2)}
Pending KYC,${data.pendingKyc}
Open Tickets,${data.openTickets}
Report Period,${DATE_RANGE_OPTIONS.find(o => o.value === dateRange)?.label}
Generated,${new Date().toISOString()}`;
  };

  const generateCustomersCSV = async () => {
    const response = await apiRequest("GET", "/api/customers");
    if (!response.ok) throw new Error("Failed to fetch customers");
    const customers = await response.json();

    const headers = "ID,First Name,Last Name,Email,Phone,KYC Status,Status,Created At";
    const rows = customers.map((c: any) =>
      `${c.id},${c.firstName || ""},${c.lastName || ""},${c.email},${c.phone},${c.kycStatus},${c.status},${c.createdAt}`
    ).join("\n");

    return `${headers}\n${rows}`;
  };

  const generateCardsCSV = async () => {
    const response = await apiRequest("GET", "/api/cards");
    if (!response.ok) throw new Error("Failed to fetch cards");
    const cards = await response.json();

    const headers = "ID,Last Four,Card Type,Card Brand,Status,Daily Limit,Monthly Limit,Created At";
    const rows = cards.map((c: any) =>
      `${c.id},${c.lastFour || ""},${c.cardType},${c.cardBrand || ""},${c.status},${(c.dailyLimitCents || 0) / 100},${(c.monthlyLimitCents || 0) / 100},${c.createdAt}`
    ).join("\n");

    return `${headers}\n${rows}`;
  };

  const generateTransactionsCSV = async () => {
    const response = await apiRequest("GET", "/api/transactions");
    if (!response.ok) throw new Error("Failed to fetch transactions");
    const transactions = await response.json();

    const headers = "ID,Card ID,Amount (AUD),Currency,Merchant,Category,Status,Created At";
    const rows = transactions.map((t: any) =>
      `${t.id},${t.cardId},${(t.amountCents || 0) / 100},${t.currency || "AUD"},${t.merchantName || ""},${t.merchantCategory || ""},${t.status},${t.createdAt}`
    ).join("\n");

    return `${headers}\n${rows}`;
  };

  const stats = [
    {
      title: "Total Customers",
      value: kpis?.totalCustomers ?? 0,
      icon: Users,
      color: "text-blue-600",
      bgColor: "bg-blue-100 dark:bg-blue-900/30",
    },
    {
      title: "Active Cards",
      value: kpis?.activeCards ?? 0,
      icon: CreditCard,
      color: "text-green-600",
      bgColor: "bg-green-100 dark:bg-green-900/30",
    },
    {
      title: "Total Transactions",
      value: kpis?.totalTransactions ?? 0,
      icon: Activity,
      color: "text-purple-600",
      bgColor: "bg-purple-100 dark:bg-purple-900/30",
    },
    {
      title: "Total Volume",
      value: formatCurrency(kpis?.totalVolumeCents ?? 0),
      icon: DollarSign,
      color: "text-emerald-600",
      bgColor: "bg-emerald-100 dark:bg-emerald-900/30",
      isFormatted: true,
    },
  ];

  const exportOptions = [
    { id: "summary", label: "Summary Report", description: "Overview KPIs and metrics", icon: BarChart3 },
    { id: "customers", label: "Customers Report", description: "All customer data", icon: Users },
    { id: "cards", label: "Cards Report", description: "All issued cards", icon: CreditCard },
    { id: "transactions", label: "Transactions Report", description: "Transaction history", icon: Activity },
  ];

  return (
    <div className="p-6 space-y-6">
      {/* Header */}
      <div className="flex items-center justify-between">
        <div>
          <h1 className="text-2xl font-display font-bold tracking-tight">Report Center</h1>
          <p className="text-muted-foreground">Analytics and reporting dashboard</p>
        </div>
        <div className="flex items-center gap-3">
          <div className="flex items-center gap-2">
            <Calendar className="h-4 w-4 text-muted-foreground" />
            <Select value={dateRange} onValueChange={(v) => setDateRange(v as DateRange)}>
              <SelectTrigger className="w-[160px]">
                <SelectValue />
              </SelectTrigger>
              <SelectContent>
                {DATE_RANGE_OPTIONS.map((option) => (
                  <SelectItem key={option.value} value={option.value}>
                    {option.label}
                  </SelectItem>
                ))}
              </SelectContent>
            </Select>
          </div>
        </div>
      </div>

      {/* KPI Cards */}
      <div className="grid gap-4 md:grid-cols-2 lg:grid-cols-4">
        {stats.map((stat) => (
          <Card key={stat.title} className="relative overflow-hidden">
            <CardContent className="p-5">
              <div className="flex items-start justify-between">
                <div className="space-y-2">
                  <p className="text-sm font-medium text-muted-foreground">{stat.title}</p>
                  {isLoading ? (
                    <Skeleton className="h-9 w-24" />
                  ) : (
                    <p className="text-3xl font-display font-bold">
                      {stat.isFormatted ? stat.value : formatCompactNumber(stat.value as number)}
                    </p>
                  )}
                </div>
                <div className={cn("p-3 rounded-xl", stat.bgColor)}>
                  <stat.icon className={cn("h-5 w-5", stat.color)} />
                </div>
              </div>
            </CardContent>
          </Card>
        ))}
      </div>

      {/* Secondary Metrics */}
      <div className="grid gap-4 md:grid-cols-2">
        <Card>
          <CardHeader className="pb-3">
            <CardTitle className="text-base">Pending Operations</CardTitle>
          </CardHeader>
          <CardContent className="space-y-3">
            <div className="flex items-center justify-between p-3 rounded-lg bg-muted/50">
              <div className="flex items-center gap-3">
                <div className="p-2 rounded-lg bg-yellow-100 dark:bg-yellow-900/30">
                  <Clock className="h-4 w-4 text-yellow-600" />
                </div>
                <div>
                  <p className="text-sm font-medium">Pending KYC Reviews</p>
                  <p className="text-xs text-muted-foreground">Awaiting verification</p>
                </div>
              </div>
              {isLoading ? (
                <Skeleton className="h-8 w-12" />
              ) : (
                <span className="text-2xl font-bold">{kpis?.pendingKyc ?? 0}</span>
              )}
            </div>
            <div className="flex items-center justify-between p-3 rounded-lg bg-muted/50">
              <div className="flex items-center gap-3">
                <div className="p-2 rounded-lg bg-red-100 dark:bg-red-900/30">
                  <Activity className="h-4 w-4 text-red-600" />
                </div>
                <div>
                  <p className="text-sm font-medium">Open Support Tickets</p>
                  <p className="text-xs text-muted-foreground">Requires attention</p>
                </div>
              </div>
              {isLoading ? (
                <Skeleton className="h-8 w-12" />
              ) : (
                <span className="text-2xl font-bold">{kpis?.openTickets ?? 0}</span>
              )}
            </div>
          </CardContent>
        </Card>

        <Card>
          <CardHeader className="pb-3">
            <CardTitle className="text-base">Transaction Trends</CardTitle>
            <CardDescription>Volume over selected period</CardDescription>
          </CardHeader>
          <CardContent className="h-32 flex items-center justify-center text-muted-foreground">
            <div className="text-center">
              <BarChart3 className="mx-auto h-10 w-10 mb-2 opacity-50" />
              <p className="text-sm">Chart visualization</p>
              <p className="text-xs">Coming in next release</p>
            </div>
          </CardContent>
        </Card>
      </div>

      {/* Export Section */}
      <Card>
        <CardHeader>
          <div className="flex items-center justify-between">
            <div>
              <CardTitle className="flex items-center gap-2">
                <FileSpreadsheet className="h-5 w-5" />
                Export Reports
              </CardTitle>
              <CardDescription>Download data as CSV files for analysis</CardDescription>
            </div>
          </div>
        </CardHeader>
        <CardContent>
          <div className="grid gap-3 md:grid-cols-2 lg:grid-cols-4">
            {exportOptions.map((option) => (
              <div
                key={option.id}
                className="flex items-center justify-between p-4 rounded-lg border bg-card hover:bg-muted/50 transition-colors"
              >
                <div className="flex items-center gap-3">
                  <div className="p-2 rounded-lg bg-primary/10">
                    <option.icon className="h-4 w-4 text-primary" />
                  </div>
                  <div>
                    <p className="text-sm font-medium">{option.label}</p>
                    <p className="text-xs text-muted-foreground">{option.description}</p>
                  </div>
                </div>
                <Button
                  size="sm"
                  variant="ghost"
                  onClick={() => handleExportCSV(option.id)}
                  disabled={isExporting}
                >
                  <Download className="h-4 w-4" />
                </Button>
              </div>
            ))}
          </div>
        </CardContent>
      </Card>
    </div>
  );
}
